import { useQuery } from '@apollo/client/react'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageLayout,
  Section,
  buttonClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { usePageTitle } from '../../app/page-title.js'
import { DashboardDocument } from '../../graphql/generated/operations.js'

import { AttemptFeed } from './AttemptFeed.js'
import { AttentionList } from './AttentionList.js'
import { DashboardFilters, type DashboardFilterValue } from './DashboardFilters.js'
import { DashboardHeader } from './DashboardHeader.js'

export type { DashboardFilterValue }

export interface DashboardViewProps {
  filter: DashboardFilterValue
  /** Route glue owns the URL; this component only says what changed. */
  onFilterChange: (next: DashboardFilterValue) => void
}

/**
 * The tutor's command centre (handoff, Organisms > Dashboard summary).
 *
 * One question: what needs doing. The rollups that used to sit under the
 * attention list — the students overview, practice performance, the headline
 * totals — were each a slower reading of rows the attention list already names
 * with the student and the practice attached, so the screen said the same thing
 * three more times in descending usefulness. They are gone rather than
 * collapsed, and nothing stands where they were.
 *
 * What survives that is the attempt feed, and only when the dashboard is
 * narrowed to one student. A feed across every student is a river; a feed for
 * Sarah is her recent work, which is a thing a tutor opens this screen to
 * read.
 *
 * One query, because the attention list and the feed have to describe the same
 * slice of data. The filter lives in the URL and goes to the server: a
 * dashboard narrowed to one student is a link a tutor can keep, and narrowing it
 * here would mean the browser holding rows the server had already decided to
 * send.
 */
export function DashboardView({ filter, onFilterChange }: DashboardViewProps) {
  const t = useTranslate()
  const language = useLanguage()
  usePageTitle(t('dashboard.title'))

  /**
   * The feed appears for exactly one student and no other selection.
   *
   * The student filter is one-or-none — a `Select` that offers "Every student" or a
   * single name — so "no student" and "exactly one" are the only two states it
   * can be in, and picking a second student replaces the first rather than
   * adding to it. Deriving this from the filter rather than tracking it
   * separately is what makes the two agree: change the student and the feed's
   * visibility, and its rows, follow in the same render.
   */
  const showRecentAttempts = filter.studentId !== undefined

  const { data, loading, error } = useQuery(DashboardDocument, {
    variables: {
      filter: {
        studentId: filter.studentId ?? null,
        practiceId: filter.practiceId ?? null,
      },
      limit: 9,
      // Not "fetch and hide": the field is left out of the request, so a
      // dashboard showing no feed carries no feed rows over the wire.
      withAttempts: showRecentAttempts,
    },
  })

  if (loading) {
    return (
      <PageLayout width="operational">
        <LoadingState className="p-0 py-8">{t('dashboard.loading')}</LoadingState>
      </PageLayout>
    )
  }
  if (error) {
    return (
      <PageLayout width="operational">
        <ErrorState className="p-0 py-8">{t('dashboard.error')}</ErrorState>
      </PageLayout>
    )
  }
  if (!data) return null

  const { tutorDashboard, myStudents } = data
  // The filter's practice options: active first, then the ones the plan has
  // set aside, whose attempts the feed still shows. Archived ones stay out.
  const myPractices = [...data.myPractices, ...data.setAsidePractices]

  // Nothing to summarise and nothing to filter: this is a new account rather
  // than a filter that matched nothing, so it gets the first-run screen.
  if (myStudents.length === 0 && myPractices.length === 0) {
    return (
      <PageLayout width="operational">
        <EmptyState
          variant="page"
          title={t('dashboard.firstPracticeTitle')}
          description={t('dashboard.firstPracticeDescription')}
          action={
            <Link
              to="/$language/practices/new"
              params={{ language }}
              className={buttonClasses({ size: 'lg' })}
            >
              {t('practice.create')}
            </Link>
          }
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout width="operational">
      {/* No "create a practice" button here. The shell pins one to every tutor
          screen, and a second copy of it on the screen below would be the same
          action twice, in the space this page owes to what needs attention. */}
      <DashboardHeader name={data.me?.tutor?.displayName ?? null} />

      {/* Above the sections rather than beside either one, because it narrows
          both: the attention list and the feed answer to one filter, so they
          can never describe different slices. The student control also decides
          whether the feed is there at all, which is a second reason it belongs
          before the page rather than inside a section of it. */}
      <section aria-labelledby="filters" className="flex flex-col gap-2">
        <h2 id="filters" className="sr-only">
          {t('dashboard.filtersHeading')}
        </h2>
        <DashboardFilters
          value={filter}
          onChange={onFilterChange}
          students={myStudents}
          practices={myPractices}
        />
      </section>

      <Section id="needs-attention" title={t('dashboard.needsAttention')}>
        <AttentionList
          items={tutorDashboard.needsAttention}
          totalCount={tutorDashboard.attentionCount}
        />
      </Section>

      {/* Full width, and last. It is the only other thing on the screen, and
          the feed lays itself out in a responsive grid, so a column beside it
          would be a column of nothing. */}
      {showRecentAttempts ? (
        <Section id="recent-attempts" title={t('dashboard.recentAttempts')}>
          <AttemptFeed
            attempts={tutorDashboard.attempts ?? []}
            emptyTitle={t('dashboard.noAttemptsTitle')}
            emptyDescription={t('dashboard.noAttemptsForFilter')}
          />
        </Section>
      ) : null}
    </PageLayout>
  )
}
